import { Component } from "react";

class ErrorBoundary extends Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Report to PostHog if the SDK is loaded on window. Fails silently if it
    // isn't installed — we must never let reporting break the fallback UI.
    if (typeof window !== "undefined" && window.posthog?.captureException) {
      try {
        window.posthog.captureException(error, {
          extra: { componentStack: errorInfo?.componentStack }
        });
      } catch (_) {
        // swallow reporter failures
      }
    }

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const { error, errorInfo } = this.state;

    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", padding: "2rem", background: "#F5F5F0"
      }}>
        <div style={{
          background: "#FFF", borderRadius: "12px", padding: "2rem",
          maxWidth: "480px", width: "100%", textAlign: "center",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "1px solid #E0E0E0"
        }}>
          <h1 style={{ color: "#1B4D3E", fontSize: "1.4rem", margin: "0 0 0.75rem" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#666", fontSize: "0.95rem", lineHeight: "1.6", margin: "0 0 1.5rem" }}>
            The page hit an unexpected error. Try reloading, or head back to the homepage.
          </p>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "0.7rem 1.4rem", background: "#1B4D3E", color: "#FFF",
                border: "none", borderRadius: "8px", fontSize: "0.9rem",
                fontWeight: "600", cursor: "pointer"
              }}
            >
              Try again
            </button>
            {/* Full-page link — reloads the app and naturally clears boundary state */}
            <a
              href="/"
              style={{
                padding: "0.7rem 1.4rem", background: "#FFF", color: "#1B4D3E",
                border: "1px solid #1B4D3E", borderRadius: "8px",
                fontSize: "0.9rem", fontWeight: "600", textDecoration: "none"
              }}
            >
              Go home
            </a>
          </div>

          {isDev && error && (
            <details style={{ marginTop: "2rem", textAlign: "left" }}>
              <summary style={{ cursor: "pointer", color: "#999", fontSize: "0.8rem" }}>
                Debug info (dev only)
              </summary>
              <pre style={{
                marginTop: "0.75rem", padding: "0.75rem",
                background: "#F8F8F5", border: "1px solid #E0E0E0", borderRadius: "6px",
                fontSize: "0.75rem", overflow: "auto", color: "#333",
                whiteSpace: "pre-wrap", wordBreak: "break-word"
              }}>
                {String(error.stack || error.message || error)}
                {errorInfo?.componentStack ? `\n\nComponent stack:${errorInfo.componentStack}` : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
